import { CumulativeEarningsHistoryItem } from '@flipster/apis/v1/dynamic-earn/types';

import type { ChartDataPoint } from '../../shared/trend-chart';

/**
 * Builds a 30-day chart timeline by combining history (D-1 ~ D-29) with today's cumulative earnings.
 * Missing dates within the window are filled with 0.
 */
export function buildChartData(
  history: CumulativeEarningsHistoryItem[],
  todayCumulativeEarnings: string,
): ChartDataPoint[] {
  const historyMap = new Map<string, number>();
  for (const item of history) {
    historyMap.set(item.date, Number(item.cumulativeEarnings));
  }

  const today = new Date();
  const points: ChartDataPoint[] = [];

  for (let i = 29; i >= 1; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const dateStr = formatUTCDate(d);
    points.push({
      date: dateStr,
      value: historyMap.get(dateStr) ?? 0,
    });
  }

  const todayStr = formatUTCDate(today);
  points.push({
    date: todayStr,
    value: Number(todayCumulativeEarnings),
  });

  return points;
}

/**
 * Builds a chart timeline of `days` length from history.
 * D-0 value comes from `todayValue` (standalone field), not from the history array.
 * Missing dates within the window are filled with 0.
 */
export function buildHistoryChartData<T extends { date: string }>(
  history: T[],
  getValue: (item: T) => number,
  days: number,
  todayValue: number,
): ChartDataPoint[] {
  const today = new Date();
  const todayStr = formatUTCDate(today);

  const historyMap = new Map<string, number>();
  for (const item of history) {
    if (item.date === todayStr) continue;
    historyMap.set(item.date, getValue(item));
  }

  const points: ChartDataPoint[] = [];
  for (let i = days - 1; i >= 1; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const dateStr = formatUTCDate(d);
    points.push({ date: dateStr, value: historyMap.get(dateStr) ?? 0 });
  }

  points.push({ date: todayStr, value: todayValue });

  return points;
}

function formatUTCDate(d: Date): string {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
